/**
 * Single source of truth for colours, typography, and reusable UI atoms
 * shared between UserInput.js and UserDashboard.js.
 *
 * Import only what you need:
 *   import { COLORS, SectionCard, MarginBadge } from './shared/designSystem';
 */

import React from 'react';
import { Box, Typography, Chip, Paper, TableCell, TableRow, TextField } from '@mui/material';
import { styled, alpha, keyframes } from '@mui/system';

// ─── 1. DESIGN TOKENS ────────────────────────────────────────────────────────

export const COLORS = {
  // Primaries
  navy:        '#0F2044',
  navyLight:   '#1A3260',
  indigo:      '#6366f1',
  indigoLight: '#818cf8',
  indigoBg:    '#eef2ff',

  // Accents
  amber:       '#F59E0B',
  amberLight:  '#FCD34D',
  amberBg:     '#fff7ed',

  // Semantic
  success:     '#10B981',
  successBg:   '#ecfdf5',
  danger:      '#EF4444',
  dangerBg:    '#fef2f2',
  warning:     '#f97316',

  // Surfaces
  surface:     '#F8FAFC',
  surfaceAlt:  '#f8f9fc',
  white:       '#FFFFFF',

  // Borders & text
  border:      '#E2E8F0',
  borderAlt:   '#e5e7eb',
  textPrimary: '#1E293B',
  textAlt:     '#111827',
  textMuted:   '#64748B',
  textFaint:   '#9ca3af',
};

export const FONT = {
  display: "'Sora', 'Nunito', 'system-ui', sans-serif",
  mono:    "'DM Mono', monospace",
};

// ─── 2. ANIMATIONS ───────────────────────────────────────────────────────────

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
`;

// ─── 3. LAYOUT PRIMITIVES ────────────────────────────────────────────────────

/**
 * Full-page shell. Matches the UserDashboard DashShell background.
 * Use as the root wrapper in both pages.
 */
export const PageShell = styled(Box)({
  fontFamily: FONT.display,
  background: COLORS.surfaceAlt,
  minHeight: '100vh',
});

/**
 * Dark gradient page header strip — used at the top of UserInput.
 * UserDashboard uses a simpler <Box> header, but can adopt this too.
 */
export const PageHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '20px 32px',
  background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 100%)`,
  boxShadow: '0 4px 24px rgba(15,32,68,0.18)',
  color: COLORS.white,
});

/**
 * Sticky top filter bar (mirrors UserDashboard StickyFilter).
 */
export const StickyFilterBar = styled(Paper)(() => ({
  position: 'sticky',
  top: 0,
  zIndex: 200,
  borderRadius: 0,
  borderBottom: `1px solid ${COLORS.borderAlt}`,
  padding: '12px 24px',
  background: 'rgba(255,255,255,0.96)',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
}));

/**
 * Card wrapper for form sections.
 * Matches the UserDashboard card style (white, subtle border + shadow).
 */
export const SectionCard = styled(Paper)({
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  marginBottom: 20,
  overflow: 'hidden',
  boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  background: COLORS.white,
  transition: 'box-shadow 0.2s',
  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
});

/**
 * Coloured header strip inside a SectionCard.
 *
 * @prop {string} accent – hex colour for the left border accent (default: indigo)
 */
export const SectionHeader = styled(Box)(({ accent }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 20px',
  background: COLORS.white,
  borderBottom: `1px solid ${COLORS.borderAlt}`,
  borderLeft: `4px solid ${accent || COLORS.indigo}`,
  fontWeight: 700,
  fontSize: 13,
  color: COLORS.textAlt,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
}));

export const SectionBody = styled(Box)({
  padding: '20px 24px',
});

// ─── 4. TABLE PRIMITIVES ─────────────────────────────────────────────────────

/** Shared table head cell style object (pass to sx prop). */
export const TH_SX = {
  fontWeight: 700,
  bgcolor: COLORS.surface,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#374151',
  whiteSpace: 'nowrap',
  borderBottom: `2px solid ${COLORS.borderAlt}`,
  py: 1.5,
};

/**
 * Zebra-striped data row.
 * @prop {number} rowIndex – used for alternating background
 */
export const DataRow = styled(TableRow)(({ rowindex }) => ({
  background: rowindex % 2 === 0 ? COLORS.white : alpha(COLORS.navy, 0.022),
  cursor: 'pointer',
  transition: 'background 0.15s',
  '&:hover': { background: alpha(COLORS.indigo, 0.05) },
  '& .MuiTableCell-body': {
    padding: '8px 10px',
    borderBottom: `1px solid ${COLORS.borderAlt}`,
    fontSize: 13,
  },
}));

/** Subtotal row (Transportation / Customization / Marketing). */
export const SubtotalRow = styled(TableRow)({
  background: alpha(COLORS.navy, 0.03),
  '& .MuiTableCell-body': {
    padding: '8px 10px',
    borderBottom: `1px solid ${COLORS.borderAlt}`,
    color: COLORS.textMuted,
    fontSize: 13,
  },
});

/** Grand total row. */
export const TotalRow = styled(TableRow)({
  background: `linear-gradient(90deg, ${alpha(COLORS.navy, 0.07)}, ${alpha(COLORS.indigo, 0.06)})`,
  '& .MuiTableCell-body': {
    padding: '12px 10px',
    fontWeight: 800,
    fontSize: 14,
    color: COLORS.navy,
    borderTop: `2px solid ${COLORS.navy}`,
    borderBottom: 'none',
  },
});

// ─── 5. REUSABLE MICRO-COMPONENTS ────────────────────────────────────────────

/**
 * Coloured margin % badge with traffic-light colouring.
 * Green ≥ 20 % · Amber ≥ 10 % · Red < 10 %
 */
export const MarginBadge = ({ percent }) => {
  const val = parseFloat(percent) || 0;
  const color =
    val >= 20 ? COLORS.success :
    val >= 10 ? COLORS.amber   :
                COLORS.danger;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ color, fontWeight: 700, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
        {val.toFixed(1)}%
      </Typography>
    </Box>
  );
};

/**
 * Formatted currency display inside a table cell body.
 * @prop {boolean} highlight – makes the value green/red based on sign
 */
export const CurrencyDisplay = ({ value, highlight = false }) => {
  const num = parseFloat(value) || 0;
  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: highlight ? 700 : 500,
        color: highlight
          ? num >= 0 ? COLORS.success : COLORS.danger
          : COLORS.textPrimary,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: FONT.mono,
        fontSize: 13,
      }}
    >
      ₹{new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)}
    </Typography>
  );
};

/**
 * Margin chip for grand total rows — colour-coded Chip.
 */
export const MarginChip = ({ percent }) => {
  const val = parseFloat(percent) || 0;
  const [color, bg] =
    val >= 20 ? [COLORS.success, alpha(COLORS.success, 0.12)] :
    val >= 10 ? ['#B45309',      alpha(COLORS.amber, 0.15)]   :
                [COLORS.danger,  alpha(COLORS.danger, 0.10)];
  return (
    <Chip
      label={`${val.toFixed(1)}%`}
      size="small"
      sx={{ fontWeight: 700, fontSize: 12, color, bgcolor: bg }}
    />
  );
};

/**
 * Status chip for order status columns.
 * Colour map matches STATUS_COLORS in UserDashboard.
 */
export const STATUS_COLORS = {
  'Delivered':                 { color: '#0284c7', bg: '#e0f2fe' },
  "PO's Released to vendors":  { color: '#65a30d', bg: '#ecfccb' },
  'Material Inward Done':      { color: '#ea580c', bg: '#ffedd5' },
  'Purchase Invoice Received': { color: '#4b5563', bg: '#f3f4f6' },
  'Branding Completed':        { color: '#4f46e5', bg: '#e0e7ff' },
  'Material Dispatched':       { color: '#e11d48', bg: '#ffe4e6' },
  'Invoice Sent to client':    { color: '#0d9488', bg: '#ccfbf1' },
  'Pending':                   { color: '#d97706', bg: '#fef3c7' },
};

export const OrderStatusChip = ({ status }) => {
  const c = STATUS_COLORS[status] || { color: '#374151', bg: '#f3f4f6' };
  return (
    <Chip
      label={status || 'N/A'}
      size="small"
      sx={{
        fontSize: 11, fontWeight: 700,
        color: c.color, background: c.bg,
        border: `1px solid ${c.color}33`,
      }}
    />
  );
};

/**
 * Aggregate summary pills: Total PP / SP / Margin.
 * Used in UserInput header and UserDashboard summary bar.
 *
 * @prop {Array<{ label, value, variant }>} stats
 *   variant: 'cost' | 'revenue' | 'profit'
 */
export const AggregateBar = ({ stats }) => (
  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
    {stats.map(({ label, value, variant }) => {
      const [bg, border, textColor] =
        variant === 'profit'  ? [alpha(COLORS.success, 0.08), alpha(COLORS.success, 0.25), COLORS.success] :
        variant === 'revenue' ? [alpha(COLORS.amber,   0.09), alpha(COLORS.amber,   0.28), '#92400e']       :
                                [alpha(COLORS.navy,    0.05), alpha(COLORS.navy,    0.15), COLORS.navy];
      return (
        <Box key={label} sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: bg, border: `1px solid ${border}`, minWidth: 120 }}>
          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: COLORS.textMuted, display: 'block' }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, color: textColor, fontFamily: FONT.mono, fontSize: 14 }}>
            {value}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/**
 * Payment terms options — identical in both files.
 */
export const PAYMENT_TERM_OPTIONS = [
  { value: 'advance',          label: 'Advance'          },
  { value: 'against-delivery', label: 'Against Delivery' },
  { value: '10-days',          label: '10 Days'          },
  { value: '15-days',          label: '15 Days'          },
  { value: '30-days',          label: '30 Days'          },
  { value: '45-days',          label: '45 Days'          },
  { value: '60-days',          label: '60 Days'          },
  { value: 'custom',           label: 'Custom…'          },
];

export const INTERNAL_CATEGORY = [
  {value: 'admin', label:"Admin"},
  {value: 'business-travel', label:"Business Travel"},
  {value: 'digital-marketing', label:"Digital Marketing"},
  {value: 'hr', label:"HR"},
  {value: 'it-admin', label:"IT Admin"},
  {value: 'packaging-material', label:"Packaging Material"},
  {value: 'rent', label:"Rent"},
  {value: 'warehouse-expenses', label:"Warehouse Expenses"},
]

/**
 * Empty state component (no search results etc.)
 */
export const EmptyState = ({ title = 'No records found', message = 'No data matches the current filters.', icon = '🔍' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, bgcolor: COLORS.surface, borderRadius: 3, border: `1px dashed ${COLORS.border}`, color: COLORS.textMuted }}>
    <Typography sx={{ fontSize: 40, mb: 1 }}>{icon}</Typography>
    <Typography variant="subtitle1" fontWeight={700} color="text.secondary">{title}</Typography>
    <Typography variant="body2" color="text.disabled">{message}</Typography>
  </Box>
);

/**
 * Shared field sx — consistent rounded inputs across both files.
 */
export const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 },
};

export const SELECT_SX = { borderRadius: '8px', fontSize: 13 };

/**
 * formatCurrency — single source of truth, no more duplicates.
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '0.0';
  return new Intl.NumberFormat('en-IN', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};